import React, {useEffect, useState} from 'react';
import {DataGrid, GridToolbar} from '@mui/x-data-grid';
import {IconButton, Box} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {styled} from '@mui/system';

function formatNumber(value, digits = 2) {
    if (typeof value === 'undefined' || value === null || value === '') {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return value;
    }
    if (num % 1 === 0 && digits === 0) {
        return num.toString().replace(/\d(?=(\d{3})+\b)/g, '$&,');
    }
    return num.toFixed(digits).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

function formatInteger(value) {
    if (typeof value === 'undefined' || value === null || value === '') {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return value;
    }
    return Math.round(num).toString().replace(/\d(?=(\d{3})+\b)/g, '$&,');
}

const DATE_COLUMN_WIDTH = 110;
const ACCOUNT_NAME_WIDTH = 220;

const StyledDataGrid = styled(DataGrid)(() => ({
    '& .MuiDataGrid-row': {
        '&:nth-child(even)': {
            backgroundColor: '#eaf0f5',
        },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
    },
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal !important',
        wordWrap: 'break-word',
    },
    '& .total-row': {
        backgroundColor: '#f8fff9',
        fontWeight: 'bold',
    },
    '& .child-row': {
        backgroundColor: '#f7f9fb',
        fontStyle: 'italic',
    },
    '& .rcpm-low, & .profit-low, & .gap-flag': {
        color: '#c62828',
        backgroundColor: 'rgba(255, 235, 235, 0.85)',
    },
    '& .rcpm-high, & .profit-high': {
        color: '#2e7d32',
        backgroundColor: 'rgba(235, 255, 235, 0.85)',
    },
    '& .MuiDataGrid-columnHeader[data-field="account_name"], & .MuiDataGrid-cell[data-field="account_name"]': {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: '#fff',
    },
    '& .MuiDataGrid-columnHeader[data-field="account_manager"], & .MuiDataGrid-cell[data-field="account_manager"]': {
        position: 'sticky',
        left: ACCOUNT_NAME_WIDTH,
        zIndex: 3,
        backgroundColor: '#fff',
    },
    '& .MuiDataGrid-columnHeader[data-field="date"], & .MuiDataGrid-cell[data-field="date"]': {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: '#fff',
    },
    '&.group-by-day .MuiDataGrid-columnHeader[data-field="account_name"], &.group-by-day .MuiDataGrid-cell[data-field="account_name"]': {
        left: DATE_COLUMN_WIDTH,
    },
    '&.group-by-day .MuiDataGrid-columnHeader[data-field="account_manager"], &.group-by-day .MuiDataGrid-cell[data-field="account_manager"]': {
        left: DATE_COLUMN_WIDTH + ACCOUNT_NAME_WIDTH,
    },
    '& .MuiDataGrid-row:nth-child(even) .MuiDataGrid-cell[data-field="account_name"], & .MuiDataGrid-row:nth-child(even) .MuiDataGrid-cell[data-field="account_manager"], & .MuiDataGrid-row:nth-child(even) .MuiDataGrid-cell[data-field="date"]': {
        backgroundColor: '#eaf0f5',
    },
    '& .total-row .MuiDataGrid-cell[data-field="account_name"], & .total-row .MuiDataGrid-cell[data-field="account_manager"], & .total-row .MuiDataGrid-cell[data-field="date"]': {
        backgroundColor: '#f8fff9',
    },
}));

const BREAKDOWN_ROWS = [
    {key: 'smarthub_revenue', label: 'SmartHub Revenue $'},
    {key: 'rixengine_revenue', label: 'RixEngine (RE) Revenue $'},
    // {key: 'aniview_revenue', label: 'Aniview (AV) Revenue $'}, // Aniview integration is not connected yet
];

function processData(data, expandedIds) {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const processed = data.map((row) => ({
        ...row.value,
        id: row.value.id,
        total: row.value.id === 'total',
        isChild: false,
    }));

    const withChildren = [];
    processed.forEach((row) => {
        withChildren.push(row);
        if (row.total) {
            return;
        }
        if (expandedIds.includes(row.id)) {
            BREAKDOWN_ROWS.forEach((item) => {
                withChildren.push({
                    id: `${row.id}-${item.key}`,
                    date: '',
                    account_name: item.label,
                    account_manager: '',
                    type: '',
                    platform: '',
                    total_internal_revenue: row[item.key],
                    isChild: true,
                    parentId: row.id,
                    total: false,
                });
            });
        }
    });

    return withChildren;
}

function GlobalReportDataGrid({data, selectedFields = [], groupBy = 'total'}) {
    const [expandedIds, setExpandedIds] = useState([]);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        setRows(processData(data, expandedIds));
    }, [data, expandedIds]);

    const toggleExpand = (id) => {
        setExpandedIds((prev) => (
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        ));
    };

    const columns = [
        {field: 'irtb_id', headerName: 'iRTB ID', width: 100},
        {
            field: 'account_name',
            headerName: 'Account Name',
            width: ACCOUNT_NAME_WIDTH,
            renderCell: (params) => (
                <span style={{paddingLeft: params.row.isChild ? 20 : 0}}>{params.value}</span>
            ),
        },
        {field: 'account_manager', headerName: 'Account Manager', width: 160},
        {field: 'type', headerName: 'Type', width: 100},
        {field: 'platform', headerName: 'Platform', width: 140},
        {
            field: 'requests',
            headerName: 'Requests',
            width: 120,
            valueFormatter: (params) => formatInteger(params.value),
        },
        {
            field: 'avg_daily_qps',
            headerName: 'Avg Daily QPS',
            width: 130,
            valueFormatter: (params) => formatNumber(params.value, 4),
        },
        {
            field: 'responses',
            headerName: 'Responses',
            width: 120,
            valueFormatter: (params) => formatInteger(params.value),
        },
        {
            field: 'impressions',
            headerName: 'Impressions',
            width: 130,
            valueFormatter: (params) => formatInteger(params.value),
        },
        {
            field: 'clicks',
            headerName: 'Clicks',
            width: 100,
            valueFormatter: (params) => formatInteger(params.value),
        },
        {
            field: 'ssp_win_rate',
            headerName: 'SSP Win Rate %',
            width: 140,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'dsp_win_rate',
            headerName: 'DSP Win Rate %',
            width: 140,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'fill_rate',
            headerName: 'Fill Rate %',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'rcpm',
            headerName: 'rCPM',
            width: 110,
            valueFormatter: (params) => formatNumber(params.value, 4),
            cellClassName: (params) => {
                if (params.row.isChild || params.value === null || params.value === undefined || params.value === '') {
                    return '';
                }
                if (params.value < 0.1) return 'rcpm-low';
                if (params.value > 1) return 'rcpm-high';
                return '';
            },
        },
        {
            field: 'dsp_ecpm',
            headerName: 'DSP eCPM',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value, 4),
        },
        {
            field: 'total_internal_revenue',
            headerName: 'Total Internal Revenue $',
            width: 210,
            renderCell: (params) => {
                if (params.row.isChild) {
                    return formatNumber(params.value);
                }
                if (params.row.total) {
                    return formatNumber(params.value);
                }
                const expanded = expandedIds.includes(params.row.id);
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <IconButton size="small" onClick={() => toggleExpand(params.row.id)}>
                            {expanded
                                ? <IndeterminateCheckBoxOutlinedIcon fontSize="small"/>
                                : <AddBoxOutlinedIcon fontSize="small"/>}
                        </IconButton>
                        <span>{formatNumber(params.value)}</span>
                    </Box>
                );
            },
        },
        {
            field: 'cost',
            headerName: 'Cost $',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'gross_profit',
            headerName: 'Gross Profit $',
            width: 140,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'margin',
            headerName: 'Margin %',
            width: 110,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'avg_daily_rev_7d',
            headerName: 'AVG Daily Rev for Last 7 Days',
            width: 200,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'avg_daily_rev_14d',
            headerName: 'AVG Daily Rev for Last 14 Days',
            width: 210,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'avg_monthly_profit',
            headerName: 'AVG Monthly Profit (Last 3 Months)',
            width: 240,
            renderCell: (params) => {
                if (params.row.isChild) {
                    return '';
                }
                const value = params.value;
                const trend = params.row.profit_trend;
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <span>{formatNumber(value)}</span>
                        {trend === 'up' && <ArrowUpwardIcon sx={{fontSize: 16, color: '#2e7d32'}}/>}
                        {trend === 'down' && <ArrowDownwardIcon sx={{fontSize: 16, color: '#c62828'}}/>}
                    </Box>
                );
            },
            cellClassName: (params) => {
                if (params.row.isChild || params.value === null || params.value === undefined || params.value === '') {
                    return '';
                }
                if (params.value < 500) return 'profit-low';
                if (params.value > 1000) return 'profit-high';
                return '';
            },
        },
        {
            field: 'api_rev',
            headerName: 'API Rev $',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'api_gap',
            headerName: 'API Gap $',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value),
        },
        {
            field: 'api_gap_pct',
            headerName: 'API Gap %',
            width: 120,
            valueFormatter: (params) => formatNumber(params.value),
            cellClassName: (params) => {
                if (params.row.isChild || params.value === null || params.value === undefined || params.value === '') {
                    return '';
                }
                if (Math.abs(Number(params.value)) > 5) {
                    return 'gap-flag';
                }
                return '';
            },
        },
    ];

    const filteredColumns = selectedFields && selectedFields.length > 0
        ? columns.filter((column) => selectedFields.includes(column.field))
        : columns;
    const visibleColumns = filteredColumns.length > 0 ? filteredColumns : columns;
    const isDayGrouping = groupBy === 'day';
    const gridColumns = isDayGrouping
        ? [{field: 'date', headerName: 'Date', width: DATE_COLUMN_WIDTH}, ...visibleColumns]
        : visibleColumns;

    if (!rows.length) {
        return <div></div>;
    }

    return (
        <div style={{height: 800, width: '100%'}}>
            <StyledDataGrid
                className={isDayGrouping ? 'group-by-day' : ''}
                getRowHeight={() => 'auto'}
                rows={rows}
                columns={gridColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                getRowClassName={(params) => {
                    if (params.row.total) return 'total-row';
                    if (params.row.isChild) return 'child-row';
                    return '';
                }}
                slots={{
                    toolbar: GridToolbar,
                }}
                disableRowSelectionOnClick
            />
        </div>
    );
}

export default GlobalReportDataGrid;
